import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Container, Row, Col, Badge } from "reactstrap";
import { userTransactionListApi } from "./_requests";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import TableContainer from "../../Components/Common/TableContainer"; // Make sure this path is correct
import { formatDate } from "../../helpers/FunctionHelpers";

interface UserSubscriptionListProps {
  refresh: boolean;
  userId?: number;
}

interface Transaction {
  id: number;
  transaction_id: string;
  user_id: number;
  wallet_id: number;
  transaction_type: string;
  order_id: number | null;
  user_subscription_id: number | null;
  credit: number;
  debit: number;
  balance: string;
  payment_status: string;
  created_at: string;
  updated_at: string;
  user: {
    id: number;
    name: string;
    email: string;
  };
  order: {
    id: number;
    order_code_id: string;
    total_amount: string;
  } | null;
  userSubscription: {
    id: number;
    gross_amount: string;
    subscription?: {
      name?: string;
    }
  } | null;
}

const UserTranactionList = ({ refresh, userId }: UserSubscriptionListProps) => {
  const { id } = useParams<{ id: string }>();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const data = { 
        user_id: userId || id,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize
      }
      const response = await userTransactionListApi(data);
      if (response.status && response.data) {
        setTransactions(response.data);
        setTotalCount(response.totalCount || 0);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [id, refresh, userId, pagination.pageIndex, pagination.pageSize]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return <Badge color="success">Completed</Badge>;
      case "PENDING":
        return <Badge color="warning">Pending</Badge>;
      case "FAILED":
        return <Badge color="danger">Failed</Badge>;
      default:
        return <Badge color="secondary">Unknown</Badge>;
    }
  };

  const getTransactionType = (type: string) => {
    switch (type) {
      case "ORDER_PAYMENT":
        return <Badge color="primary">Order Payment</Badge>;
      case "SUBSCRIPTION_PAYMENT":
        return <Badge color="info">Subscription Payment</Badge>;
      case "1":
      case "Top-up":
        return <Badge color="success">Top-up</Badge>;
      case "2":
      case "Withdraw":
        return <Badge color="danger">Withdraw</Badge>;
      case "REFUND":
        return <Badge color="warning">Refund</Badge>;
      default:
        return <Badge color="secondary">{type}</Badge>;
    }
  };



  const formatCurrency = (amount: string | number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(Number(amount));
  };

  // Define columns for the TableContainer
  const columns = [
    {
      header: "#",
      accessorKey: "id",
      cell: (cellProps: any) => {
        const { pageIndex, pageSize } = cellProps.table.getState().pagination;
        return pageIndex * pageSize + cellProps.row.index + 1;
      },
    },
    {
      header: "Transaction ID",
      accessorKey: "transaction_id",
      cell: (cellProps: any) => {
        return <strong>{cellProps.row.original.transaction_id}</strong>;
      },
    },
    {
      header: "Type",
      accessorKey: "transaction_type",
      cell: (cellProps: any) => {
        return getTransactionType(cellProps.row.original.transaction_type);
      },
    },
    {
      header: "Amount",
      accessorKey: "amount",
      cell: (cellProps: any) => {
        const transaction = cellProps.row.original;
        return (
          <div>
            {transaction.debit > 0 ? (
              <span className="text-danger">- {formatCurrency(transaction.debit)}</span>
            ) : (
              <span className="text-success">+ {formatCurrency(transaction.credit)}</span>
            )}
            <br />
            <small className="text-muted">Balance: {formatCurrency(transaction.balance)}</small>
          </div>
        );
      },
    },
    {
      header: "Reference",
      accessorKey: "reference",
      cell: (cellProps: any) => {
        const transaction = cellProps.row.original;
        if (transaction.transaction_type === "ORDER_PAYMENT" && transaction.order) {
          return (
            <div>
              <strong>Order:</strong> {transaction.order.order_code_id}
              <br />
              <strong>Amount:</strong> {formatCurrency(transaction.order.total_amount)}
            </div>
          );
        } else if (transaction.transaction_type === "SUBSCRIPTION_PAYMENT" && transaction.userSubscription) {
          return (
            <div>
              <strong>{transaction?.userSubscription?.subscription?.name || 'Subscription'}</strong>
              <br />
              <strong>Amount:</strong> {formatCurrency(transaction.userSubscription.gross_amount)}
            </div>
          );
        }
        return "N/A";
      },
    },
    {
      header: "Status",
      accessorKey: "payment_status",
      cell: (cellProps: any) => {
        return getStatusBadge(cellProps.row.original.payment_status);
      },
    },
    {
      header: "Date",
      accessorKey: "created_at",
      cell: (cellProps: any) => {
        return formatDate(cellProps.row.original.created_at);
      },
    },
  ];

  return (
    <div className="">
      <Container fluid>
        <Row>
          <Col lg={12}>
            {loading ? (
              <div className="text-center">Loading...</div>
            ) : transactions.length > 0 ? (
              <TableContainer
                columns={columns}
                data={transactions}
                isGlobalFilter={true}
                customPageSize={pagination.pageSize}
                manualPagination={true}
                rowCount={totalCount}
                pageCount={Math.ceil(totalCount / pagination.pageSize)}
                onPaginationChange={setPagination}
                paginationState={pagination}
                divClass="table-responsive mb-1"
                tableClass="mb-0 table-bordered"
                theadClass="table-light text-primary fw-bold" // Added text-primary and fw-bold
              />
            ) : (
              <div className="text-center fw-bold py-5">
                No transaction data found
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default UserTranactionList;